import { FoodsIconButton } from "@foodpilot/foods";
import { useTheme } from "@mui/material";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { UnitConversion } from "src/api/client-api/post/postSchema";
import { UnitConverterOption } from "src/utils/buildUnitConverterOptions";
import { findConversionSourceOption } from "src/utils/unitConversionRecord";
import { formatConversionFactor } from "src/utils/formatting";
import { useQuestionnaireFormContext } from "../../context/useQuestionnaireForm";

export const unitConverterElementIds = (converterKey: string) => ({
    button: `unit-converter-button-${converterKey}`,
    panel: `unit-converter-panel-${converterKey}`,
});

type Props = {
    converterKey: string;
    disabled: boolean;
    options: UnitConverterOption[];
    unitConversion: UnitConversion | null;
    targetUnit: string;
};

export const UnitConverterButton = (props: Props) => {
    const { converterKey, disabled, options, unitConversion, targetUnit } = props;
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const { openUnitConverterKey, setOpenUnitConverterKey } = useQuestionnaireFormContext();

    const locale = i18n.resolvedLanguage ?? "en";
    const isOpen = openUnitConverterKey === converterKey;
    const ids = unitConverterElementIds(converterKey);
    const openLabel = t("ssq.unit_converter.open_tooltip", "Convert from another unit");

    const convertedFrom =
        unitConversion &&
        t(
            "ssq.unit_converter.converted_from",
            "Converted from {{value}} {{source}} (1 {{unit}} = {{factor}} {{target}})",
            {
                value: new Intl.NumberFormat(locale, { maximumFractionDigits: 20 }).format(
                    Number(unitConversion.sourceValue),
                ),
                source:
                    findConversionSourceOption(options, unitConversion)?.label ?? unitConversion.sourceUnitAbbreviation,
                unit: unitConversion.sourceUnitAbbreviation,
                factor: formatConversionFactor(unitConversion.factor, locale),
                target: targetUnit,
            },
        );

    useEffect(() => {
        if (disabled && isOpen) {
            setOpenUnitConverterKey(null);
        }
    }, [disabled, isOpen, setOpenUnitConverterKey]);

    return (
        <FoodsIconButton
            size="s"
            icon="unitConverter"
            withBackground
            withStatusDot={unitConversion !== null}
            tooltip={{ text: convertedFrom ? <span>{convertedFrom}</span> : openLabel }}
            buttonProps={{
                id: ids.button,
                disabled: disabled && !convertedFrom,
                "aria-disabled": disabled || undefined,
                "aria-label": convertedFrom ? `${openLabel}. ${convertedFrom}` : openLabel,
                "aria-expanded": isOpen,
                "aria-controls": isOpen ? ids.panel : undefined,
                onClick: () => {
                    if (!disabled) {
                        setOpenUnitConverterKey(isOpen ? null : converterKey);
                    }
                },
                sx: isOpen ? { background: theme.custom.grey[600], borderColor: theme.custom.grey[600] } : undefined,
            }}
        />
    );
};
